import React from 'react';
import PropTypes from 'prop-types';
import useSimpleHousehold from 'Hooks/useSimpleHousehold';
import { editQuestionnaire, mapSectionToPage, mapBeneficiaryToSection } from 'Consts/Questionnaire';
import ShadowBox from 'Components/UI/ShadowBox';
import EditButtonLink from 'Components/Blueprint/UI/EditButtonLink';
import {
  nonRetirementAccounts as enumeratedValuesNonRetirementAccounts,
  personalRetirementAccounts as enumeratedValuesPersonalRetirementAccounts,
  employerSponsoredPlans as enumeratedValuesEmployerSponsoredPlans,
  lifeInsurancePolicies as enumeratedValuesLifeInsurancePolicies,
  otherAdvisors as enumeratedValuesotherAdvisors,
} from 'Consts/EnumeratedValues';
import ImageHeading from '../ImageHeading';

function getEditButtonBeneficiary(simpleHousehold, beneficiary, isAgent) {
  if (simpleHousehold) {
    const section = mapBeneficiaryToSection(beneficiary.type || 'Other');
    const page = mapSectionToPage(section);
    const link = editQuestionnaire(page, {
      section,
      isAgent,
      householdId: simpleHousehold.householdResource.id(),
    });

    return (
      <EditButtonLink
        title="Edit Beneficiary"
        to={link}
      />
    );
  }
  return null;
}
function getEditButtonAdvisor(simpleHousehold, advisor, isAgent) {
  if (simpleHousehold) {
    const page = mapSectionToPage(advisor.attributes.type);
    const link = editQuestionnaire(page, {
      isAgent,
      section: advisor.attributes.type,
      householdId: simpleHousehold.householdResource.id(),
    });

    return (
      <EditButtonLink
        title="Edit Advisor"
        to={link}
      />
    );
  }
  return null;
}

function shapeBeneficiaries(
  nonRetirementAccounts,
  personalRetirementAccounts,
  employerSponsoredPlans,
  definedPensionPlans,
  groupLifeInsuranceAccounts,
  lifeInsuranceAccounts,
  simpleHousehold,
  isAgent,
) {
  const accountsByBeneficiary = [];

  /* normalize array */
  if (nonRetirementAccounts.length) {
    nonRetirementAccounts.forEach((account) => {
      if (
        account.attributes.owner !== ''
        && account.attributes.hasBeneficiary
        && (account.attributes.primaryBeneficiary !== ''
          || account.attributes.contingentBeneficiary !== '')
      ) {
        if (accountsByBeneficiary[account.attributes.owner] === undefined) {
          accountsByBeneficiary[account.attributes.owner] = [];
        }
        accountsByBeneficiary[account.attributes.owner].push({
          id: `${account.id}nra`,
          type: enumeratedValuesNonRetirementAccounts(
            'type',
            account.attributes.type,
          ),
          primary: account.attributes.primaryBeneficiary,
          contingent: account.attributes.contingentBeneficiary,
        });
      }
    });
  }

  if (personalRetirementAccounts.length) {
    personalRetirementAccounts.forEach((account) => {
      if (
        account.attributes.owner !== ''
        && account.attributes.hasBeneficiary
        && (account.attributes.primaryBeneficiary !== ''
          || account.attributes.contingentBeneficiary !== '')
      ) {
        if (accountsByBeneficiary[account.attributes.owner] === undefined) {
          accountsByBeneficiary[account.attributes.owner] = [];
        }
        accountsByBeneficiary[account.attributes.owner].push({
          id: `${account.id}pra`,
          type: enumeratedValuesPersonalRetirementAccounts(
            'type',
            account.attributes.type,
          ),
          primary: account.attributes.primaryBeneficiary,
          contingent: account.attributes.contingentBeneficiary,
        });
      }
    });
  }

  if (employerSponsoredPlans.length) {
    employerSponsoredPlans.forEach((account) => {
      if (
        account.attributes.owner !== ''
        && account.attributes.hasBeneficiary
        && (account.attributes.primaryBeneficiary !== ''
          || account.attributes.contingentBeneficiary !== '')
      ) {
        if (accountsByBeneficiary[account.attributes.owner] === undefined) {
          accountsByBeneficiary[account.attributes.owner] = [];
        }
        accountsByBeneficiary[account.attributes.owner].push({
          id: `${account.id}esp`,
          type: enumeratedValuesEmployerSponsoredPlans(
            'type',
            account.attributes.type,
          ),
          primary: account.attributes.primaryBeneficiary,
          contingent: account.attributes.contingentBeneficiary,
        });
      }
    });
  }

  if (definedPensionPlans.length) {
    definedPensionPlans.forEach((account) => {
      if (
        account.attributes.accountOwner !== ''
        && account.attributes.beneficiaryElections
        && (account.attributes.beneficiaryPrimary !== ''
          || account.attributes.beneficiaryContingent !== '')
      ) {
        if (
          accountsByBeneficiary[account.attributes.accountOwner] === undefined
        ) {
          accountsByBeneficiary[account.attributes.accountOwner] = [];
        }
        accountsByBeneficiary[account.attributes.accountOwner].push({
          id: `${account.id}dpp`,
          type:
            account.attributes.financialInstitution !== ''
              ? `Pension - ${account.attributes.financialInstitution}`
              : 'Pension',
          primary: account.attributes.beneficiaryPrimary,
          contingent: account.attributes.beneficiaryContingent,
        });
      }
    });
  }

  if (groupLifeInsuranceAccounts.length) {
    groupLifeInsuranceAccounts.forEach((account) => {
      if (
        account.attributes.insuredClientName !== ''
        && account.attributes.hasBeneficiary
        && (account.attributes.primaryBeneficiary !== ''
          || account.attributes.contingentBeneficiary !== '')
      ) {
        if (accountsByBeneficiary[account.attributes.insuredClientName] === undefined) {
          accountsByBeneficiary[account.attributes.insuredClientName] = [];
        }
        accountsByBeneficiary[account.attributes.insuredClientName].push({
          id: `${account.attributes.id}glia`,
          type: 'Group Life',
          primary: account.attributes.primaryBeneficiary,
          contingent: account.attributes.contingentBeneficiary,
        });
      }
    });
  }

  if (lifeInsuranceAccounts.length) {
    lifeInsuranceAccounts.forEach((account) => {
      if (
        account.attributes.insuredClientName !== ''
        && account.attributes.hasBeneficiary
        && (account.attributes.primaryBeneficiary !== ''
          || account.attributes.contingentBeneficiary !== '')
      ) {
        if (accountsByBeneficiary[account.attributes.insuredClientName] === undefined) {
          accountsByBeneficiary[account.attributes.insuredClientName] = [];
        }
        accountsByBeneficiary[account.attributes.insuredClientName].push({
          id: account.attributes.id,
          type: enumeratedValuesLifeInsurancePolicies(
            'type',
            account.attributes.type,
          ),
          primary: account.attributes.primaryBeneficiary,
          contingent: account.attributes.contingentBeneficiary,
        });
      }
    });
  }

  const whateverArray = [];
  Object.keys(accountsByBeneficiary).forEach(key => accountsByBeneficiary[key].forEach((value, i) => {
    whateverArray.push(
      <tr className="data" key={`${value.id}row`}>
        <td className={`client client${i}`}>
          {key}
          {getEditButtonBeneficiary(simpleHousehold, value, isAgent)}
        </td>
        <td className="value type">
          {value.type || 'Other'}
        </td>
        <td className="value primary">{value.primary}</td>
        <td className="value contingent">{value.contingent}</td>
      </tr>,
    );
  }));

  /* build the beneficiary table */
  if (Object.keys(accountsByBeneficiary).length) {
    // return some stuff!
    return (
      <table>
        <tbody>
          <tr className="headings">
            <td>&nbsp;</td>
            <td>&nbsp;</td>
            <td>Primary Beneficiary(ies)</td>
            <td>Contingent Beneficiary(ies)</td>
          </tr>
          {whateverArray}
        </tbody>
      </table>
    );
  }

  return (
    <table>
      <tbody>
        <tr>
          <td>No beneficiary accounts.</td>
        </tr>
      </tbody>
    </table>
  );
}

const BeneficiarySummary = ({
  nonRetirementAccounts,
  personalRetirementAccounts,
  employerSponsoredPlans,
  definedPensionPlans,
  groupLifeInsuranceAccounts,
  lifeInsuranceAccounts,
  otherAdvisors,
  isAgent,
}) => {
  const simpleHousehold = useSimpleHousehold();

  return (
    <table className="beneficiary-summary-etc">
      <tbody>
        <tr>
          <td className="beneficiary-summary">
            <ImageHeading className="beneficiary-summary large">
              Beneficiary Summary
            </ImageHeading>
            {shapeBeneficiaries(
              nonRetirementAccounts,
              personalRetirementAccounts,
              employerSponsoredPlans,
              definedPensionPlans,
              groupLifeInsuranceAccounts,
              lifeInsuranceAccounts,
              simpleHousehold,
              isAgent,
            )}
          </td>
          {otherAdvisors.length > 0 && (
            <td className="ancillary-advisors">
              <ShadowBox>
                <h4>Ancillary Advisors</h4>
                {otherAdvisors.map(advisor => (
                  <div className="advisor" key={`${advisor.id}adv`}>
                    <div className="type">
                      {enumeratedValuesotherAdvisors(
                        'type',
                        advisor.attributes.type,
                      )}
                      {getEditButtonAdvisor(simpleHousehold, advisor, isAgent)}
                    </div>
                    {advisor.attributes.name !== '' && (
                      <div>{advisor.attributes.name}</div>
                    )}
                    {advisor.attributes.firm !== '' && (
                      <div>
                        {advisor.attributes.firm}
                        {advisor.attributes.lengthOfRelationship !== null && (
                          <span>
                            &nbsp;-&nbsp;
                            {advisor.attributes.lengthOfRelationship}
                            &nbsp;year(s)
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </ShadowBox>
            </td>
          )}
        </tr>
      </tbody>
    </table>
  );
};

BeneficiarySummary.propTypes = {
  nonRetirementAccounts: PropTypes.array.isRequired,
  personalRetirementAccounts: PropTypes.array.isRequired,
  employerSponsoredPlans: PropTypes.array.isRequired,
  definedPensionPlans: PropTypes.array.isRequired,
  groupLifeInsuranceAccounts: PropTypes.array.isRequired,
  lifeInsuranceAccounts: PropTypes.array.isRequired,
  otherAdvisors: PropTypes.array.isRequired,
  isAgent: PropTypes.bool.isRequired,
};

export default BeneficiarySummary;
